import type { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';

import { bandColor, bandLabel, bandRanges } from '../growth';
import { GROWTH } from '../tokens';
import type { Cell, SchemeLevel } from '../types';

export const GROWTH_CARD_SX = {
  bgcolor: GROWTH.cardBg,
  border: `1px solid ${GROWTH.divider}`,
  borderRadius: '12px',
  p: '28px 32px 24px',
  boxSizing: 'border-box',
  width: '100%',
} as const;

export const TEXT_SX = { fontFamily: GROWTH.font, fontSize: '16px', lineHeight: '24px', fontWeight: 500, color: GROWTH.ink } as const;
export const SMALL_SX = { fontFamily: GROWTH.font, fontSize: '12px', lineHeight: '16px', fontWeight: 500, color: GROWTH.muted } as const;
export const STRONG_SX = { ...TEXT_SX, fontWeight: 700 } as const;

export const HEADER_ROW_SX = {
  display: 'flex',
  alignItems: 'flex-end',
  gap: '16px',
  pb: '8px',
  borderBottom: `2px solid ${GROWTH.ink}`,
} as const;

export const HEADER_CELL_SX = {
  fontFamily: GROWTH.font,
  fontSize: '12px',
  lineHeight: '16px',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: GROWTH.muted,
  whiteSpace: 'nowrap',
} as const;

export function OpenChip() {
  return (
    <Box
      component="span"
      data-testid="open-chip"
      sx={{ display: 'inline-block', px: '8px', borderRadius: '9999px', bgcolor: GROWTH.open.bg, color: GROWTH.open.fg, fontFamily: GROWTH.font, fontSize: '12px', lineHeight: '20px', fontWeight: 700, whiteSpace: 'nowrap' }}
    >
      Open
    </Box>
  );
}

export function CardHead({ title, sub, open, right, legend }: { title: string; sub?: string; open?: boolean; right?: ReactNode; legend?: SchemeLevel[] }) {
  return (
    <Box sx={{ pb: '16px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '16px' }}>
        <Box>
          <Typography component="h2" sx={{ display: 'flex', alignItems: 'center', gap: '12px', fontFamily: GROWTH.font, fontSize: '20px', lineHeight: '28px', fontWeight: 700, color: GROWTH.ink, m: 0 }}>
            {title}
            {open && <OpenChip />}
          </Typography>
          {sub !== undefined && (
            <Typography component="p" sx={{ ...TEXT_SX, color: GROWTH.muted, m: '4px 0 0' }}>
              {sub}
            </Typography>
          )}
        </Box>
        {right}
      </Box>
      {legend && <LevelLegend scheme={legend} />}
    </Box>
  );
}

export function LevelLegend({ scheme }: { scheme: SchemeLevel[] }) {
  return (
    <Box data-testid="level-legend" sx={{ display: 'flex', flexWrap: 'wrap', columnGap: '16px', rowGap: '4px', pt: '8px' }}>
      {bandRanges(scheme).map((level) => (
        <Typography key={level.band} component="span" data-testid="legend-item" sx={{ ...TEXT_SX, color: GROWTH.muted, display: 'inline-flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
          <BandDot scheme={scheme} band={level.band} size={12} />
          {`${level.range} ${level.label}`}
        </Typography>
      ))}
    </Box>
  );
}

export function scoreText(cell: Cell): string {
  return cell.value === undefined ? '' : cell.value.toFixed(1);
}

export function BandDot({ scheme, band, size = 10, color }: { scheme: SchemeLevel[]; band: number; size?: number; color?: string }) {
  return (
    <Box
      component="span"
      aria-hidden="true"
      sx={{ width: size, height: size, borderRadius: '9999px', flexShrink: 0, display: 'inline-block', bgcolor: color ?? colorOf(scheme, band) }}
    />
  );
}

export function colorOf(scheme: SchemeLevel[], band: number): string {
  return bandColor(scheme, band) || GROWTH.bands[band - 1] || GROWTH.disabled;
}

export function labelOf(scheme: SchemeLevel[], band: number): string {
  return bandLabel(scheme, band);
}

export function bandNames(scheme: SchemeLevel[]): string[] {
  return [1, 2, 3, 4, 5].map((band) => labelOf(scheme, band));
}

export function Chip({ bg, fg, children, testId }: { bg: string; fg: string; children: ReactNode; testId?: string }) {
  return (
    <Box
      component="span"
      data-testid={testId}
      sx={{ display: 'inline-block', px: '8px', borderRadius: '9999px', bgcolor: bg, color: fg, fontFamily: GROWTH.font, fontSize: '12px', lineHeight: '20px', fontWeight: 700, whiteSpace: 'nowrap' }}
    >
      {children}
    </Box>
  );
}
